import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, Calendar, FileText, Mail, TrendingUp, BarChart3, DollarSign, Building2, Users, PieChart, ChevronDown, ChevronRight, X, User, Circle, LogOut } from 'lucide-react';

// ✅ Hook custom qui encapsule useLocation de manière sûre
function useSafeLocation() {
  try {
    return useLocation();
  } catch {
    return { pathname: "/" };
  }
}

const Sidebar = ({ isOpen, onClose }) => {
  // Toujours appelé → pas de violation ESLint
  const location = useSafeLocation();
  const navigate = useNavigate();
  const [expandedMenus, setExpandedMenus] = useState({
    'donnees-annuelles': false,
    'echanges': false
  });
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [clientInfo, setClientInfo] = useState(null);

  // Récupérer les informations du client depuis localStorage
  useEffect(() => {
    const storedClientInfo = localStorage.getItem('client_info');
    if (storedClientInfo) {
      try {
        setClientInfo(JSON.parse(storedClientInfo));
      } catch (error) {
        console.error('Erreur lors du parsing des informations client:', error);
      }
    }
  }, []);

  const toggleMenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);

    localStorage.removeItem('client_token');
    localStorage.removeItem('client_info');
    localStorage.removeItem('user');
    localStorage.removeItem('role');

    await new Promise(resolve => setTimeout(resolve, 800));

    navigate('/login');

    if (onClose) onClose();
  };


  const menuItems = [
    {
      id: 'echanges',
      label: 'Échanges d\'informations',
      icon: MessageCircle,
      path: '/dashboard-user',
      subItems: [
        { id: 'echeances', label: 'Échéances fiscales', icon: Calendar, path: '/dashboard-user/echeances' },
        { id: 'fichiers', label: 'Échange de fichiers', icon: FileText, path: '/dashboard-user/fichiers' },
        // { id: 'messagerie', label: 'Messagerie', icon: Mail, path: '/dashboard-user/messagerie' }
      ]
    },
    {
      id: 'donnees-mensuelles',
      label: 'Données mensuelles',
      icon: TrendingUp,
      path: '/donnees-mensuelles'
    },
    // {
    //   id: 'donnees-annuelles',
    //   label: 'Données annuelles',
    //   icon: BarChart3,
    //   path: '/donnees-annuelles',
    //   subItems: [
    //     { id: 'performances', label: 'Performances & Rentabilité', icon: DollarSign, path: '/donnees-annuelles/performances' },
    //     { id: 'tresorerie', label: 'Trésorerie', icon: TrendingUp, path: '/donnees-annuelles/tresorerie' },
    //     { id: 'structure-financiere', label: 'Structure financière', icon: Building2, path: '/donnees-annuelles/structure-financiere' },
    //     { id: 'immobilisations', label: 'Immobilisations', icon: PieChart, path: '/donnees-annuelles/immobilisations' },
    //     { id: 'fiscal-social', label: 'Données fiscales & sociales', icon: Users, path: '/donnees-annuelles/fiscal-social' },
    //     { id: 'autres-donnees', label: 'Autres données', icon: FileText, path: '/donnees-annuelles/autres' }
    //   ]
    // }
  ];

  // Vérifie si un item est actif
  const isItemActive = (item) => {
    const currentPath = location?.pathname || '/';
    if (item.path === currentPath) return true;
    if (item.path && currentPath.startsWith(item.path + '/')) return true;
    if (item.subItems && item.subItems.length > 0) {
      return item.subItems.some(subItem => isItemActive(subItem));
    }
    return false;
  };

  // Auto-expansion des menus actifs
  useEffect(() => {
    const currentPath = location?.pathname || '/';
    if (currentPath.startsWith('/donnees-annuelles/')) {
      setExpandedMenus(prev => ({ ...prev, 'donnees-annuelles': true }));
    }
    if (currentPath.startsWith('/dashboard-user/') && currentPath !== '/dashboard-user') {
      setExpandedMenus(prev => ({ ...prev, 'echanges': true }));
    }
  }, [location?.pathname]);

  const MenuItem = ({ item, level = 0 }) => {
    const hasSubItems = item.subItems && item.subItems.length > 0;
    const isExpanded = expandedMenus[item.id];
    const active = isItemActive(item);
    const Icon = item.icon;

    if (hasSubItems) {
      return (
        <div className={`menu-item ${active ? 'active' : ''}`}>
          <button
            className={`menu-button ${level > 0 ? 'sub' : ''}`}
            onClick={() => toggleMenu(item.id)}
            type="button"
          >
            <div className="menu-content">
              <Icon size={20} />
              <span>{item.label}</span>
            </div>
            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </button>
          <div className={`submenu ${isExpanded ? 'expanded' : ''}`}>
            {item.subItems.map((subItem) => (
              <MenuItem key={subItem.id} item={subItem} level={level + 1} />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className={`menu-item ${active ? 'active' : ''}`}>
        <NavLink
          to={item.path}
          className={({ isActive }) =>
            `menu-button link ${level > 0 ? 'sub' : ''} ${isActive ? 'active' : ''}`
          }
          onClick={() => {
            if (onClose) onClose();
          }}
        >
          <div className="menu-content">
            <Icon size={20} />
            <span>{item.label}</span>
          </div>
        </NavLink>
      </div>
    );
  };

  return (
    <>
      {/* Overlay mobile */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose} />}

      {/* Sidebar */}
      <aside className={`sidebar ${isOpen ? 'open' : ''}`}>
        {/* Header mobile */}
        <div className="sidebar-header-mobile">
          <h2>Menu Navigation</h2>
          <button onClick={onClose} className="close-btn">
            <X size={24} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="menu-item">
            <NavLink
              to="/dashboard-user"
              className={({ isActive }) => `menu-button link ${isActive ? 'active' : ''}`}
              onClick={() => {
                if (onClose) onClose();
              }}
            >
              <div className="menu-content">
                <BarChart3 size={20} />
                <span>Tableau de bord</span>
              </div>
            </NavLink>
          </div>

          {menuItems.map((item) => (
            <MenuItem key={item.id} item={item} />
          ))}
        </nav>

        {/* Informations du client */}
        {clientInfo && (
          <div className="sidebar-user">
            <div className="user-avatar">
              <User size={24} />
            </div>
            <div className="user-info">
              <p className="user-name">{clientInfo.nom || clientInfo.name || 'Client'}</p>
              <p className="user-email">{clientInfo.email}</p>
              <p className="user-role">Client CINEF</p>
            </div>
            <div className="user-status-indicator">
              <Circle size={8} className="user-status" />
              <span className="status-text">En ligne</span>
            </div>
          </div>
        )}

        {/* Bouton de déconnexion */}
        <div className="sidebar-logout">
          <button
            className="logout-btn"
            onClick={handleLogout}
            disabled={isLoggingOut}
            type="button"
          >
            {isLoggingOut ? (
              <>
                <div className="spinner" />
                <span>Déconnexion...</span>
              </>
            ) : (
              <>
                <LogOut size={20} />
                <span>Se déconnecter</span>
              </>
            )}
          </button>
        </div>
      </aside>

      {/* ✅ CSS intégré */}
      <style jsx>{`
        .sidebar-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.5);
          z-index: 40;
          display: none;
        }

        .sidebar {
          position: fixed;
          left: 0;
          top: 0;
          bottom: 0;
          width: 280px;
          background: white;
          border-right: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          z-index: 50;
          transition: transform 0.3s ease;
        }

        .sidebar-header-mobile {
          display: none;
          padding: 1rem;
          border-bottom: 1px solid #e5e7eb;
          align-items: center;
          justify-content: space-between;
        }

        .sidebar-header-mobile h2 {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0;
        }

        .close-btn {
          background: none;
          border: none;
          cursor: pointer;
          padding: 0.25rem;
          color: #6b7280;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .close-btn:hover {
          color: #111827;
        }

        .sidebar-nav {
          flex: 1;
          overflow-y: auto;
          padding: 1rem 0;
        }

        .menu-item {
          margin: 0.25rem 0.75rem;
        }

        .menu-button {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0.75rem 1rem;
          border-radius: 0.5rem;
          border: none;
          background: none;
          cursor: pointer;
          color: #4b5563;
          font-size: 0.875rem;
          font-weight: 500;
          text-decoration: none;
          transition: all 0.2s;
        }

        .menu-button:hover {
          background: #f3f4f6;
          color: #111827;
        }

        .menu-button.active {
          background: #dbeafe;
          color: #1e40af;
        }

        .menu-button.sub {
          padding-left: 2.5rem;
          font-size: 0.8125rem;
        }

        .menu-content {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .submenu {
          max-height: 0;
          overflow: hidden;
          transition: max-height 0.3s ease;
        }

        .submenu.expanded {
          max-height: 500px;
        }

        .sidebar-user {
          display: flex;
          align-items: flex-start;
          gap: 0.75rem;
          padding: 1rem;
          border-top: 1px solid #e5e7eb;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
          margin: 0.5rem;
          border-radius: 0.75rem;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
        }

        .user-avatar {
          width: 2.5rem;
          height: 2.5rem;
          border-radius: 9999px;
          background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #1e40af;
          flex-shrink: 0;
          box-shadow: 0 2px 4px rgba(30, 64, 175, 0.2);
        }

        .user-info {
          flex: 1;
          min-width: 0;
        }

        .user-name {
          font-size: 0.875rem;
          font-weight: 600;
          color: #111827;
          margin: 0 0 0.25rem 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-email {
          font-size: 0.75rem;
          color: #4b5563;
          margin: 0 0 0.25rem 0;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }

        .user-role {
          font-size: 0.75rem;
          color: #6b7280;
          margin: 0;
        }

        .user-status-indicator {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.25rem;
        }

        .user-status {
          color: #10b981;
          fill: #10b981;
        }

        .status-text {
          font-size: 0.75rem;
          color: #10b981;
          font-weight: 500;
        }

        .sidebar-logout {
          padding: 1rem;
          border-top: 1px solid #e5e7eb;
        }

        .logout-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          padding: 0.875rem 1rem;
          border-radius: 0.5rem;
          border: none;
          background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
          color: white;
          font-size: 0.875rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 2px 8px rgba(220, 38, 38, 0.2);
        }

        .logout-btn:hover:not(:disabled) {
          background: linear-gradient(135deg, #b91c1c 0%, #991b1b 100%);
          box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
          transform: translateY(-1px);
        }

        .logout-btn:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: 0 2px 6px rgba(220, 38, 38, 0.2);
        }

        .logout-btn:disabled {
          cursor: not-allowed;
          opacity: 0.8;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 3px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        @media (max-width: 768px) {
          .sidebar {
            transform: translateX(-100%);
          }

          .sidebar.open {
            transform: translateX(0);
          }

          .sidebar-overlay {
            display: block;
          }

          .sidebar-header-mobile {
            display: flex;
          }

          .sidebar-user {
            margin: 0.25rem;
            padding: 0.75rem;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 0.5rem;
          }

          .user-avatar {
            width: 3rem;
            height: 3rem;
          }

          .user-info {
            width: 100%;
          }

          .user-name {
            font-size: 0.875rem;
            text-align: center;
          }

          .user-email {
            font-size: 0.75rem;
            text-align: center;
          }

          .user-role {
            text-align: center;
          }

          .user-status-indicator {
            flex-direction: row;
            gap: 0.5rem;
          }
        }
      `}</style>
    </>
  );
};

export default Sidebar;